// PitchSubmissionForm.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import BaseForm from './BaseForm';
import apiService from '../services/apiService';
import { v4 as uuidv4 } from 'uuid';
import countries from '../data/countries';
import { Upload, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';

const FileDropZone = ({ label, id, onChange, accept = "*", required = false, description = "" }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const fileInputRef = useRef(null);
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  }, [isDragging]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      setFileName(file.name);
      setFileSize(formatFileSize(file.size));
      
      // Create a fake event to pass to the onChange handler
      const event = { 
        target: { 
          name: id, 
          files: e.dataTransfer.files 
        } 
      };
      onChange(event);
    }
  }, [id, onChange]);

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      setFileSize(formatFileSize(file.size));
    }
    onChange(e);
  };

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveFile = () => {
    setFileName('');
    setFileSize('');
    fileInputRef.current.value = '';
    // Create a fake event to clear the file
    const event = { 
      target: { 
        name: id, 
        files: null
      } 
    };
    onChange(event);
  };

  return (
    <div className="mb-6">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      {description && (
        <p className="text-sm text-gray-500 mb-2">{description}</p>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        id={id}
        name={id}
        onChange={handleFileInputChange}
        className="hidden"
        accept={accept}
      />
      
      {!fileName ? (
        <div
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed p-6 rounded-lg text-center cursor-pointer transition-colors ${
            isDragging ? 'border-custom-red bg-red-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onClick={handleBrowseClick}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            Drag & drop your file here, or click to browse
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Supported formats: PDF, DOC, DOCX (Max size: 10MB)
          </p>
        </div>
      ) : (
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-10 w-10 bg-custom-red rounded-md flex items-center justify-center">
                <Check className="h-6 w-6 text-white" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                  {fileName}
                </p>
                <p className="text-xs text-gray-500">{fileSize}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="text-gray-400 hover:text-red-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TermsModal = ({ show, onClose, onAccept, setTermsRead }) => {
  const [enableAccept, setEnableAccept] = useState(false);
  
  // Use useEffect to enable the button after a reasonable time
  useEffect(() => {
    if (show) {
      // Enable the button after 5 seconds (adjust as needed)
      const timer = setTimeout(() => {
        setEnableAccept(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [show]);
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white w-full h-full md:h-[95vh] md:w-[95vw] md:rounded-lg md:mx-auto md:my-auto shadow-2xl flex flex-col">
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <h3 className="text-xl font-semibold">Pitch Process & Terms and Conditions</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex-grow overflow-auto p-0">
          <iframe 
            src="/Pitch Process & Terms and Conditions.pdf" 
            className="w-full h-full border-0"
            title="Terms and Conditions"
          />
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {!enableAccept ? 
              "Please review the terms and conditions..." : 
              "Thank you for reviewing our terms and conditions."}
          </div>
          <button 
            onClick={() => {
              if (setTermsRead) setTermsRead(true);
              onAccept();
              onClose();
            }}
            disabled={!enableAccept}
            className={`px-4 py-2 rounded ${
              enableAccept 
                ? 'bg-custom-red text-white hover:bg-red-600' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {enableAccept ? 'I Agree' : 'Please review...'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PitchSubmissionForm = ({ onClose, initialFormData, referenceCode: initialReferenceCode }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Applicant Information
    fullName: '',
    organizationName: '',
    email: '',
    phone: '',
    country: '',
    
    // Project Information
    projectTitle: '',
    projectType: '',
    projectDescription: '',
    videoUrl: '',
    
    // New fields to match database schema
    genre: '',
    developmentalStage: '',
    targetAudience: '',
    teamInformation: '',
    marketPotential: '',
    uniqueSellingPoint: '',
    logline: '',
    synopsis: '',
    director: '',
    producer: '',
    budget: '',
    
    // Project Documents
    projectProposalFile: null,
    financialProjectionsFile: null,
    fundingStatus: '',
    distributionDealsFile: null,
    infrastructurePlansFile: null,
    distributionStrategiesFile: null,
    
    // Terms and Conditions
    termsAccepted: false,
    subscribeToNewsletter: true, // Default checked
    termsAcceptedAt: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [referenceCode, setReferenceCode] = useState('');
  const [showLoadForm, setShowLoadForm] = useState(false);
  const [loadReferenceCode, setLoadReferenceCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [draftReferenceCode, setDraftReferenceCode] = useState('');
  const [draftError, setDraftError] = useState('');
  const [termsRead, setTermsRead] = useState(false);
  const navigate = useNavigate();

  // Add useEffect to initialize form with saved data when available
  useEffect(() => {
    if (initialFormData && Object.keys(initialFormData).length > 0) {
      console.log('Initializing form with saved data:', initialFormData);
      
      // Convert string file names back to file objects
      const processedData = { ...initialFormData };
      
      // Set the form data
      setFormData(processedData);
      
      // Set application ID from the reference code if provided
      if (initialReferenceCode) {
        setApplicationId(initialReferenceCode);
        setReferenceCode(initialReferenceCode);
      }
      
      // Show success message
      setSuccess('Your saved application has been loaded successfully.');
    }
  }, [initialFormData, initialReferenceCode]);

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  // Handle file uploads
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    }
  };

  // Navigation functions
  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 4));
    setError('');
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  // Validation function for each step
  const validateStep = (step) => {
    switch (step) {
      case 1: // Applicant Information
        if (!formData.fullName) return 'Please enter your full name';
        if (!formData.organizationName) return 'Please enter your organization name';
        if (!formData.email) return 'Please enter your email address';
        if (!formData.phone) return 'Please enter your phone number';
        if (!formData.country) return 'Please select your country of residence';
        return '';
      
      case 2: // Project Information
        if (!formData.projectTitle) return 'Please enter your project title';
        if (!formData.projectType) return 'Please select a project type';
        if (!formData.projectDescription) return 'Please provide a project description';
        return '';
      
      case 3: // Project Documents
        if (!formData.projectProposalFile) return 'Please upload a detailed project proposal';
        if (!formData.financialProjectionsFile) return 'Please upload financial projections and ROI analysis';
        if (!formData.fundingStatus) return 'Please describe your current funding status';
        return '';
      
      case 4: // Terms and Conditions
        if (!formData.termsAccepted) return 'You must accept the terms and conditions to proceed';
        return '';
      
      default:
        return '';
    }
  };

  // Handle step changes with validation
  const handleStepChange = (direction) => {
    if (direction === 'next') {
      const error = validateStep(currentStep);
      if (error) {
        setError(error);
        return;
      }
      nextStep();
    } else {
      prevStep();
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.termsAccepted) {
      setError('You must accept the terms and conditions to submit your application');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Create FormData object for file uploads
      const formDataForSubmission = new FormData();
      
      // Append text fields
      Object.keys(formData).forEach(key => {
        // Skip null/undefined values and file fields
        if (formData[key] != null && !key.includes('File')) {
          formDataForSubmission.append(key, formData[key]);
        }
      });
      
      // Append file fields
      if (formData.projectProposalFile) {
        formDataForSubmission.append('projectProposalFile', formData.projectProposalFile);
      }
      if (formData.financialProjectionsFile) {
        formDataForSubmission.append('financialProjectionsFile', formData.financialProjectionsFile);
      }
      if (formData.distributionDealsFile) {
        formDataForSubmission.append('distributionDealsFile', formData.distributionDealsFile);
      }
      if (formData.infrastructurePlansFile) {
        formDataForSubmission.append('infrastructurePlansFile', formData.infrastructurePlansFile);
      }
      if (formData.distributionStrategiesFile) {
        formDataForSubmission.append('distributionStrategiesFile', formData.distributionStrategiesFile);
      }
      
      // Use the reference code if available
      if (referenceCode) {
        formDataForSubmission.append('referenceCode', referenceCode);
      }
      
      console.log('Submitting pitch application...');
      const response = await apiService.submitPitchApplication(formDataForSubmission);
      
      if (response.success) {
        // Get the server-generated reference code
        const serverRefCode = response.data.referenceCode;
        
        // Store reference code for later use
        setReferenceCode(serverRefCode);
        
        // Save to session storage
        sessionStorage.setItem('pitchReferenceCode', serverRefCode);
        
        // Save user info to localStorage
        const userInfo = {
          name: formData.fullName,
          email: formData.email
        };
        localStorage.setItem('pitchUserInfo', JSON.stringify(userInfo));
        
        setIsSubmitting(false);
        setSuccess('Your pitch application has been submitted successfully! You will now be redirected to select your AFFF badge.');
        
        // Create the URL with proper encoding
        const redirectUrl = `/pitch-badge-selection?ref=${encodeURIComponent(serverRefCode)}&name=${encodeURIComponent(formData.fullName)}&email=${encodeURIComponent(formData.email)}`;
        
        // Log the redirect URL for debugging
        console.log('Will redirect to:', redirectUrl);
        
        // Use a more reliable approach for redirection
        setTimeout(() => {
          // First try using navigate
          try {
            navigate(redirectUrl);
          } catch (navError) {
            console.error('Navigation error:', navError);
            // Fallback to window.location if navigate fails
            window.location.href = redirectUrl;
          }
        }, 3000);
      } else {
        throw new Error(response.message || 'Unknown error occurred');
      }
    } catch (error) {
      console.error("Error submitting pitch form:", error);
      setError(error.message || "Failed to submit form. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Generate reference code for saving progress
  const generateReferenceCode = () => {
    const shortUuid = uuidv4().split('-')[0];
    const dateStr = new Date().toISOString().slice(0,10).replace(/-/g, '');
    const projectIdentifier = formData.projectTitle 
      ? formData.projectTitle.replace(/[^a-zA-Z0-9]/g, '').substring(0, 5).toUpperCase() 
      : `${formData.fullName.split(' ')[0].substring(0, 5)}`.toUpperCase();
      
    return `PITCH-${dateStr}-${projectIdentifier}-${shortUuid}`;
  };

  // Update saveProgress function with more robust state handling
  const saveProgress = async () => {
    if (!formData.email) {
      setError('Please enter your email address to save your progress');
      return;
    }
    
    // Generate the code first and store it in a local variable
    const code = applicationId || generateReferenceCode();
    console.log('Generated reference code:', code);
    
    // Set application ID if needed
    if (!applicationId) {
      setApplicationId(code);
    }
    
    // Immediately update the reference code state
    setReferenceCode(code);
    
    // Store in local storage immediately as a backup
    localStorage.setItem('pitchReferenceCode', code);
    
    try {
      // Convert file objects to file names for storage
      const fileData = { ...formData };
      
      // Store file names instead of file objects
      if (fileData.projectProposalFile) fileData.projectProposalFile = fileData.projectProposalFile.name;
      if (fileData.financialProjectionsFile) fileData.financialProjectionsFile = fileData.financialProjectionsFile.name;
      if (fileData.fundingStatus) fileData.fundingStatus = fileData.fundingStatus;
      if (fileData.distributionDealsFile) fileData.distributionDealsFile = fileData.distributionDealsFile?.name;
      if (fileData.infrastructurePlansFile) fileData.infrastructurePlansFile = fileData.infrastructurePlansFile?.name;
      if (fileData.distributionStrategiesFile) fileData.distributionStrategiesFile = fileData.distributionStrategiesFile?.name;
      
      console.log('About to save pitch draft with reference code:', code);
      
      // Display the modal BEFORE making the API call to avoid timing issues
      setTimeout(() => {
        console.log('Setting showReferenceModal to true');
        setShowReferenceModal(true);
      }, 500);
      
      // Add a backup alert in case modal still fails
      setTimeout(() => {
        if (document.querySelectorAll('.fixed.inset-0.bg-black.bg-opacity-50').length === 0) {
          console.log('No modal found, showing alert fallback');
          alert(`Your application has been saved successfully!\n\nReference code: ${code}\n\nPlease copy this code - you'll need it to resume your application.`);
        }
      }, 2000);
      
      // Make API call
      await apiService.savePitchDraft({
        type: 'pitch',
        referenceCode: code,
        formData: fileData,
        timestamp: new Date().toISOString(),
        sendEmailNotification: true,
        userEmail: formData.email
      });
      
      setError('');
      setSuccess(`Progress saved successfully! Your reference code is: ${code}`);
      
      // Force a direct DOM modal as an additional failsafe
      showFallbackModal(code);
      
    } catch (error) {
      console.error('Error saving progress:', error);
      setError('Failed to save your progress. Please try again.');
      
      // Still show the reference if we have one, even if save failed
      if (code) {
        alert(`We encountered an error saving your progress, but your reference code is: ${code}\n\nPlease save this code to resume your application later.`);
      }
    }
  };

  // Add this helper function for a failsafe modal
  const showFallbackModal = (code) => {
    // Create a modal div if it doesn't exist
    let modalDiv = document.getElementById('fallback-reference-modal');
    if (!modalDiv) {
      modalDiv = document.createElement('div');
      modalDiv.id = 'fallback-reference-modal';
      document.body.appendChild(modalDiv);
    }
    
    // Set the content
    modalDiv.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: white; padding: 20px; border-radius: 8px; max-width: 90%; width: 400px;">
          <h3 style="font-size: 18px; font-weight: bold; margin-bottom: 12px;">Your Application Reference</h3>
          <p style="margin-bottom: 12px;">Your application has been saved. Please use this reference code to resume your application later:</p>
          <div style="background: #f3f4f6; padding: 12px; border: 1px solid #ccc; text-align: center; font-family: monospace; font-weight: bold; font-size: 16px; margin-bottom: 16px;">
            ${code}
          </div>
          <p style="font-size: 14px; margin-bottom: 16px;"><strong>Important:</strong> Please save this reference code. You will need it to resume your application.</p>
          <div style="display: flex; justify-content: space-between;">
            <button onclick="navigator.clipboard.writeText('${code}'); alert('Reference code copied!');" style="padding: 8px 16px; background: #e5e7eb; border-radius: 4px; cursor: pointer; border: none;">
              Copy Code
            </button>
            <button onclick="document.getElementById('fallback-reference-modal').remove();" style="padding: 8px 16px; background: #dc2626; color: white; border-radius: 4px; cursor: pointer; border: none;">
              Close
            </button>
          </div>
        </div>
      </div>
    `;
  };

  // Update the renderReferenceModal function for better debugging
  const renderReferenceModal = () => {
    if (!showReferenceModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full">
          <h3 className="text-xl font-bold mb-4">Your Application Reference</h3>
          <p className="mb-3">Your application has been saved. Please use this reference code to resume your application later:</p>
          
          <div className="bg-gray-100 p-3 rounded border text-center font-mono font-bold text-lg mb-4">
            {referenceCode}
          </div>
          
          <p className="text-sm mb-4">
            <strong>Important:</strong> Please save this reference code. You will need it to resume your application.
          </p>
          
          <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
            <button
              onClick={() => {
                navigator.clipboard.writeText(referenceCode);
                alert('Reference code copied to clipboard!');
              }}
              className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Copy Code
            </button>
            
            <button
              onClick={() => setShowReferenceModal(false)}
              className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Close
            </button>
            
            <button
              onClick={() => {
                navigate(`/pitch-badge-selection?ref=${referenceCode}`);
              }}
              className="px-4 py-2 bg-custom-red text-white rounded-md hover:bg-red-600"
            >
              Get Badge Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Update loadSavedApplication to handle file references
  const loadSavedApplication = async (referenceCode) => {
    try {
      setIsLoading(true);
      const response = await apiService.getPitchDraft(referenceCode.trim());
      
      if (response.data && response.data.success) {
        const savedData = response.data.data.formData;
        console.log('Retrieved application data:', savedData);
        
        // Process the saved data similarly to useEffect
        const processedData = { ...savedData };
        
        // Set the form data
        setFormData(processedData);
        
        // Set application ID
        setApplicationId(referenceCode);
        
        setShowLoadForm(false);
        setSuccess('Your saved application has been loaded successfully.');
      } else {
        setLoadError('Failed to retrieve your application. Please check your reference code.');
      }
    } catch (error) {
      console.error('Error loading saved application:', error);
      setLoadError('Failed to retrieve your application. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Render form steps
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Applicant Information</h3>
            
            <div>
              <label className="block mb-1 font-medium">Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            
            <div>
              <label className="block mb-1 font-medium">Organization Name *</label>
              <input
                type="text"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            
            <div>
              <label className="block mb-1 font-medium">Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            
            <div>
              <label className="block mb-1 font-medium">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            
            <div>
              <label className="block mb-1 font-medium">Country of Residence *</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Project Information</h3>
            
            <div>
              <label className="block mb-1 font-medium">Project Title *</label>
              <input
                type="text"
                name="projectTitle"
                value={formData.projectTitle}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            
            <div>
              <label className="block mb-1 font-medium">Project Type *</label>
              <select
                name="projectType"
                value={formData.projectType}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Select Project Type</option>
                <option value="Film Production">Film Production</option>
                <option value="Film Infrastructure">Film Infrastructure</option>
                <option value="Film Distribution">Film Distribution</option>
              </select>
            </div>
            
            <div>
              <label className="block mb-1 font-medium">Project Description (200 words) *</label>
              <textarea
                name="projectDescription"
                value={formData.projectDescription}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                rows={6}
                maxLength={2000}
                required
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">
                {formData.projectDescription.length}/2000 characters
              </p>
            </div>
            
            <div>
              <label className="block mb-1 font-medium">Brief 5-Minute Video About Project (URL)</label>
              <input
                type="text"
                name="videoUrl"
                value={formData.videoUrl}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                placeholder="YouTube or Vimeo URL (optional but recommended)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Submitting a video increases your chances of selection
              </p>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Project Documents Upload</h3>
            
            <FileDropZone
              label="Detailed Project Proposal"
              id="projectProposalFile"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              required
              description="Include concept, storyline, target audience, and creative vision"
            />

            <FileDropZone
              label="Financial Projections and ROI Analysis"
              id="financialProjectionsFile"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              required
              description="Detailed budget, expected returns, and financial planning"
            />

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Funding Status and Investment Needs
                <span className="text-red-500">*</span>
              </label>
              <textarea
                name="fundingStatus"
                value={formData.fundingStatus || ''}
                onChange={handleInputChange}
                rows={4}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm 
                          focus:border-custom-red focus:ring focus:ring-custom-red focus:ring-opacity-20 
                          sm:text-sm p-3 resize-none hover:border-gray-400 transition duration-150"
                placeholder="Describe your current funding status, including any secured investments and your remaining investment needs."
                required
              ></textarea>
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>

            <FileDropZone
              label="Pre-Existing Distribution Deals or Partnerships (if applicable)"
              id="distributionDealsFile"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              description="Any existing distribution agreements or partnerships"
            />

            {formData.projectType === 'Film Infrastructure' && (
              <FileDropZone
                label="Infrastructure Development Plans"
                id="infrastructurePlansFile"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                description="Detailed plans for infrastructure development projects"
              />
            )}
            
            {formData.projectType === 'Film Distribution' && (
              <FileDropZone
                label="Distribution Strategies and Market Outreach Plans"
                id="distributionStrategiesFile"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                description="Marketing, distribution, and outreach strategies"
              />
            )}
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Terms and Conditions Agreement</h3>
            
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="mb-4">
                By checking the box below, you confirm that you have read and agree to the AFFF Pitch Process & Terms and Conditions. Upon submission, you will be redirected to register for the AFFF Summit and acquire your AFFF Premium Elite Plus Badge (mandatory for participation).
              </p>
              
              <div className="flex items-start mt-4">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={formData.termsAccepted || false}
                  onChange={(e) => {
                    if (e.target.checked && !termsRead) {
                      // If user is trying to check without reading terms
                      e.preventDefault();
                      setShowTermsModal(true);
                      return;
                    }
                    handleInputChange({
                      target: {
                        name: 'termsAccepted',
                        value: e.target.checked
                      }
                    });
                  }}
                  className="h-4 w-4 mt-1 text-custom-red focus:ring-custom-red border-gray-300 rounded"
                  required
                />
                <label htmlFor="termsAccepted" className="ml-2 block text-sm text-gray-700">
                  I have read and agree to the <button 
                    type="button" 
                    onClick={() => setShowTermsModal(true)} 
                    className="text-custom-red underline"
                  >
                    Terms and Conditions
                  </button>.
                  {!termsRead && formData.termsAccepted && (
                    <span className="text-red-500 ml-1 text-xs">Please read the terms first</span>
                  )}
                </label>
              </div>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-md border-l-4 border-yellow-400">
              <h4 className="font-bold">AFFF Summit Badge Information</h4>
              <p className="mt-2">
                Upon submission, you will be redirected to register for the AFFF Summit and acquire your AFFF Premium Elite Plus Badge (This is Mandatory).
              </p>
              <p className="mt-2">
                The badge grants you a range of exclusive benefits, including pre-event privileges, event-day advantages, and post-event perks.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="subscribeToNewsletter"
                  checked={formData.subscribeToNewsletter}
                  onChange={(e) => handleInputChange({
                    target: {
                      name: 'subscribeToNewsletter',
                      value: e.target.checked
                    }
                  })}
                  className="mr-2"
                />
                <span>Subscribe to our newsletter for updates on film funding opportunities and industry events</span>
              </label>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  // Function to show a simple notification since we don't have toast
  const showNotification = (message, isError = false) => {
    if (isError) {
      console.error(message);
      alert(`Error: ${message}`);
    } else {
      console.log(message);
      alert(message);
    }
  };

  // If you have retrieveDraft function, update it too
  const retrieveDraft = async (referenceCode) => {
    setIsLoading(true);
    try {
      console.log('Retrieving pitch draft with reference code:', referenceCode.trim());
      
      // Use the new pitch-specific endpoint
      const response = await apiService.getPitchDraft(referenceCode.trim());
      
      if (response.data && response.data.success) {
        const draftData = response.data.data.formData;
        setFormData(prevState => ({
          ...prevState,
          ...draftData
        }));
        
        showNotification('Pitch draft loaded successfully!');
        setDraftError('');
      } else {
        setDraftError(response.data?.message || 'Failed to retrieve pitch draft with this reference code');
        showNotification('Failed to load pitch draft', true);
      }
    } catch (error) {
      console.error('Error retrieving pitch draft:', error);
      
      if (error.response && error.response.status === 404) {
        setDraftError('No pitch draft found with this reference code. Please check and try again.');
      } else {
        setDraftError(`Error: ${error.response?.data?.message || error.message}`);
      }
      
      showNotification('Failed to load pitch draft', true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle draft retrieval
  const handleRetrieveDraft = (e) => {
    e.preventDefault();
    
    if (!draftReferenceCode || draftReferenceCode.trim() === '') {
      setDraftError('Please enter a reference code');
      return;
    }
    
    // Log the reference code that's being used
    console.log('Attempting to retrieve draft with code:', draftReferenceCode);
    
    retrieveDraft(draftReferenceCode);
  };

  // Add this console log in your render function to debug state
  useEffect(() => {
    console.log('Modal states updated - showReferenceModal:', showReferenceModal, 'referenceCode:', referenceCode);
  }, [showReferenceModal, referenceCode]);

  // Add this helper function to your component
  const displayFileInfo = (fieldName, fileName) => {
    if (!fileName) return;
    
    // Create a placeholder for the file field
    const fileFieldElement = document.getElementById(fieldName);
    if (fileFieldElement) {
      // Find the closest container that would show the file name
      const container = fileFieldElement.closest('.file-dropzone-container');
      if (container) {
        // Update the UI to show the file was previously uploaded
        const fileNameDisplay = container.querySelector('.file-name-display');
        if (fileNameDisplay) {
          fileNameDisplay.textContent = `Previously uploaded: ${fileName}`;
        }
      }
    }
  };

  // Add this to your useEffect
  useEffect(() => {
    if (initialFormData) {
      // After setting form data, update UI for file fields
      if (initialFormData.projectProposalFile) 
        displayFileInfo('projectProposalFile', initialFormData.projectProposalFile);
      if (initialFormData.financialProjectionsFile) 
        displayFileInfo('financialProjectionsFile', initialFormData.financialProjectionsFile);
      if (initialFormData.distributionDealsFile) 
        displayFileInfo('distributionDealsFile', initialFormData.distributionDealsFile);
      // ... handle other file fields
    }
  }, [initialFormData]);

  // Add this at the top of the component
  useEffect(() => {
    // Log when component mounts
    console.log('PitchSubmissionForm mounted');
    
    return () => {
      // Log when component unmounts
      console.log('PitchSubmissionForm unmounted');
    };
  }, []);

  // Add this to track form data changes
  useEffect(() => {
    console.log('Form data updated:', formData);
  }, [formData]);

  // Add this to track submission state
  useEffect(() => {
    console.log('Submission state:', { isSubmitting, error, success });
  }, [isSubmitting, error, success]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <BaseForm onClose={onClose} title="AFFF Pitch Opportunities Registration">
        <form onSubmit={handleSubmit} className="p-6">
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              {[1, 2, 3, 4].map(step => (
                <div 
                  key={step}
                  className={`text-xs font-medium ${
                    currentStep >= step ? 'text-custom-red' : 'text-gray-400'
                  }`}
                >
                  Step {step}
                </div>
              ))}
            </div>
            <div className="h-2 bg-gray-200 rounded-full">
              <div 
                className="h-2 bg-custom-red rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 4) * 100}%` }}
              ></div>
            </div>
          </div>
          
          {/* Form step content */}
          {renderStep()}
          
          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700">
              <p>{error}</p>
            </div>
          )}
          
          {/* Success message */}
          {success && (
            <div className="mt-4 p-3 bg-green-50 border-l-4 border-green-500 text-green-700">
              <p>{success}</p>
            </div>
          )}
          
          {/* Resume application button */}
          <div className="mt-4 mb-6">
            <button
              type="button"
              onClick={() => setShowLoadForm(true)}
              className="text-custom-red hover:underline text-sm"
            >
              Already started an application? Click here to resume
            </button>
          </div>
          
          {/* Navigation buttons */}
          <div className="mt-8 flex justify-between">
            {/* Left side button - Previous or Cancel */}
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={() => handleStepChange('prev')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Previous
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
            
            {/* Right side buttons - grouped Save and Next/Submit */}
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={saveProgress}
                className="px-4 py-2 bg-[#212121] text-white rounded-md hover:bg-gray-800"
              >
                Save Progress
              </button>
              
              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={() => handleStepChange('next')}
                  className="px-6 py-2 bg-custom-red text-white rounded-md hover:bg-red-600"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-6 py-2 bg-custom-red text-white rounded-md ${
                    isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:bg-red-600'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>
          </div>
        </form>
      </BaseForm>
      
      {/* Reference Code Modal - Make sure this is OUTSIDE the BaseForm component */}
      {renderReferenceModal()}
      
      {/* Load Application Form */}
      {showLoadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Load Saved Application</h3>
            <p className="mb-3">Enter your reference code to continue a saved application:</p>
            
            <div className="mb-4">
              <input 
                type="text" 
                value={loadReferenceCode}
                onChange={(e) => setLoadReferenceCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter reference code"
              />
            </div>
            
            {loadError && (
              <div className="mb-4 p-2 bg-red-50 text-red-700 border-l-4 border-red-500">
                {loadError}
              </div>
            )}
            
            <div className="flex justify-between">
              <button
                onClick={() => setShowLoadForm(false)}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              
              <button
                onClick={() => loadSavedApplication(loadReferenceCode)}
                className="px-4 py-2 bg-custom-red text-white rounded-md hover:bg-red-600"
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Load Application'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <TermsModal 
          show={showTermsModal}
          onClose={() => setShowTermsModal(false)}
          setTermsRead={setTermsRead}
          onAccept={() => {
            setFormData(prev => ({
              ...prev,
              termsAccepted: true
            }));
          }}
        />
      )}
    </div>
  );
};

export default PitchSubmissionForm;